"use client"
import { motion } from "motion/react"
import { useHudContext, useHudDates } from "../hud/shared/playbookHUDInfra"

type AnimateYearProps = {
  onlyYear?: boolean,
  // When true, the children are shown from the start regardless of the animated
  // date — bypasses the date threshold so the section is visible from the
  // beginning (the data inside still updates as the year animates).
  forceVisible?: boolean,
  // The overflow-hidden below exists for the height-collapse animation, but it
  // also clips anything taller than the settled content — e.g. the capability
  // ladder takeover painting over a shorter lower HUD. Opting in releases the
  // clip once the reveal animation has finished (and restores it for the
  // collapse), so oversized overlays can extend past the section's footprint.
  overflowVisibleWhenSettled?: boolean,
  children: React.ReactNode,
} & (
  | { targetYear: number, targetDate?: never }
  | { targetDate: Date, targetYear?: never }
)

export const AnimateYear = ({
  targetYear,
  targetDate,
  onlyYear,
  forceVisible,
  overflowVisibleWhenSettled,
  children,
}: AnimateYearProps) => {
  const { animatedDate } = useHudDates()
  const threshold = targetDate ?? new Date(targetYear!, 0, 1)
  const nextThreshold = new Date(threshold.getFullYear() + 1, threshold.getMonth(), threshold.getDate())
  const isVisible = forceVisible
    ? true
    : onlyYear
      ? animatedDate >= threshold && animatedDate < nextThreshold
      : animatedDate >= threshold
  return (
    <motion.div
      initial={false}
      animate={{
        opacity: isVisible ? 1 : 0,
        height: isVisible ? "auto" : 0,
        // transitionEnd applies AFTER the tween settles, so the grow/collapse
        // itself always runs clipped.
        ...(overflowVisibleWhenSettled
          ? { transitionEnd: { overflow: isVisible ? "visible" : "hidden" } }
          : {}),
      }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="overflow-hidden"
    >
      {children}
    </motion.div>
  )
}
